/**
 * Vitrine de profissionais do perfil.
 *
 * Mostra quem atende no estabelecimento, com foto, especialidade e avaliacao.
 * Cada card leva ao wizard na primeira etapa, sem carregar a escolha (DEC-2 da
 * spec da vitrine), entao o rotulo acessivel informa quem e e diz que o toque
 * comeca o agendamento — sem prometer que aquele profissional fica escolhido.
 */

import Link from "next/link";
import { FiImage, FiStar } from "react-icons/fi";
import type { PublicProvider } from "../types";
import { resolveImageUrl } from "../utils";
import {
  getProviderInitials,
  resolveProviderRating,
  resolveProviderSpecialization,
} from "../utils/publicPeople";
import { PreviewCarousel } from "./PreviewCarousel";

interface ProfileProvidersPreviewProps {
  providers: PublicProvider[];
  bookingHref: string;
}

export function ProfileProvidersPreview({
  providers,
  bookingHref,
}: ProfileProvidersPreviewProps) {
  if (providers.length === 0) return null;

  return (
    <section
      aria-label="Profissionais do estabelecimento"
      data-testid="profile-providers-preview"
      className="rounded-2xl bg-white px-3 py-4 shadow-sm"
    >
      <div className="mb-1 px-1">
        <h2 className="text-base font-bold text-[#233055]">Quem vai te atender</h2>
        <p className="mt-0.5 text-sm text-slate-600">
          Toque em um profissional para começar seu agendamento.
        </p>
      </div>

      <PreviewCarousel
        items={providers}
        getKey={(provider) => provider.id}
        renderItem={(provider) => {
          const specialization = resolveProviderSpecialization(provider.description);
          const rating = resolveProviderRating(
            provider.averageRating,
            provider.reviewsCount
          );
          const imageSrc = provider.imageUrl ? resolveImageUrl(provider.imageUrl) : "";

          return (
            <Link
              href={bookingHref}
              data-testid={`profile-provider-card-${provider.id}`}
              aria-label={`${provider.name}, ${specialization}${
                rating ? `, nota ${rating.label}` : ", ainda sem avaliações"
              }. Toque para começar o agendamento.`}
              className="flex h-full flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-center transition hover:border-primary-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
            >
              <span className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-lg font-bold text-slate-700">
                {imageSrc ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={imageSrc}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                ) : provider.name ? (
                  <span aria-hidden="true">{getProviderInitials(provider.name)}</span>
                ) : (
                  <FiImage className="h-7 w-7 text-slate-400" aria-hidden="true" />
                )}
              </span>

              <div className="min-w-0" aria-hidden="true">
                <p className="line-clamp-2 text-base font-semibold leading-tight text-[#233055]">
                  {provider.name}
                </p>
                <p className="mt-0.5 line-clamp-1 text-sm text-slate-600">
                  {specialization}
                </p>
                {rating ? (
                  <p className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-amber-700">
                    <FiStar className="h-4 w-4" aria-hidden="true" />
                    {rating.label}
                  </p>
                ) : (
                  <p className="mt-1 text-xs font-medium text-slate-400">
                    Ainda sem avaliações
                  </p>
                )}
              </div>
            </Link>
          );
        }}
        ariaLabel="Profissionais em destaque"
        previousLabel="Ver profissional anterior"
        nextLabel="Ver próximo profissional"
        itemWidthClass="w-[52%] sm:w-[30%]"
        testId="profile-providers-carousel"
      />
    </section>
  );
}
